/* eslint-disable camelcase */
import * as Pioneer from "pioneer";
import {
  Entity,
  Placemarks,
  SceneHelpers,
  LabelQuadtree,
} from "pioneer-scripts";
import PLACEMARK_DATA from "../data/placemark_ar";
import PLACEMARK_DATA_REGIONS from "../data/placemark_ar_regions";
import PLACEMARK_DATA_EARTHQUAKES from "../data/earthquakes";
import { getDemModelById } from "../data/dem";
import DATA_READER_DATA from "../data/value_definitions_ar"; // importing the Arabic units
import {
  SPACECRAFT_ATMOSPHERE,
  SPACECRAFT_SEA,
  SPACECRAFT_LAND,
  TRAIL_COLORS,
} from "../data/spacecraft_data_ar";
import {
  eventsStore,
  poiStore,
  gmapStore,
  layersStore,
  lightStore,
  datasetStore,
  spacecraftStore,
  appStore,
  uiStore,
  cameraStore,
  spoutStore,
  videoStore,
} from "../managers/globalState";
import globalRefs from "../managers/globalRefs";
import { filterEvents, getLimitedLatestEvents } from "../helpers/processEvents";
import Loading, { hideLoading } from "../components/loading";
import { filterPoI, getLimitedPois } from "../helpers/processPoI";
import { filterGmap, getLimitedGmaps } from "../helpers/processGmap";
import ar from "../languages/ar";
import ENTITY_INFO from "../data/entity_info.json";
import { Config } from "../config/config";
import { SAND_EVENT_IDS } from "../config/constants";

class SceneManager {
  /**
   * Constructor
   * @param {object} pioneer Pioneer engine
   * @private
   */
  constructor(pioneer) {
    // Set pioneer and scene.
    this._pioneer = pioneer;
    this._scene = pioneer.getScene("main");

    // TODO: router ref.
    // this._router = router;

    this._operatingSC = null;
    this._futureSC = null;

    // Options are fixed variables that should not be changed (frozen)
    this._options = Object.freeze({
      eventPlacemarks: {
        limit: Infinity,
        idPrefix: "event_",
      },
      poiPlacemarks: {
        limit: Infinity,
        idPrefix: "poi_",
      },
      gmapPlacemarks: {
        limit: Infinity,
        idPrefix: "gmap_",
      },
    });

    this._displayingEvents = [];
    this._displayingPois = [];
    this._displayingGmaps = [];
    this._vitalsign = "";
    this._groupId = 0;
    this._loadingItems = [];
    this._dataValueObject = null;
    this.groundTracksEntity = null;
    this._trailWidth = 2;

    this._enabledSC = null;
    this._demModelLocalPosition = null;
    this._demModelLocalOrientation = null;
    this._demPlacemarkLocalPosition = null;
    this._demPlacemarkLocalOrientation = null;
    this._demTransformSyncInstalled = false;
    this._labelQuadtree = null;

    // Bind handlers.
    this._onEventsStateUpdate = this._onEventsStateUpdate.bind(this);
    this._onPoIStateUpdate = this._onPoIStateUpdate.bind(this);
    this._onGmapStateUpdate = this._onGmapStateUpdate.bind(this);
    this._onLightStateUpdate = this._onLightStateUpdate.bind(this);
    this._onCityNamesStateUpdate = this._onCityNamesStateUpdate.bind(this);
    this._onregionsStateUpdate = this._onregionsStateUpdate.bind(this);
    this._onEarthquakesStateUpdate = this._onEarthquakesStateUpdate.bind(this);
    this.startEarthquakeTimeline = this.startEarthquakeTimeline.bind(this);
    this._onDemStateUpdate = this._onDemStateUpdate.bind(this);
    this._onDemModelIdStateUpdate = this._onDemModelIdStateUpdate.bind(this);
    this._onGroundTrackStateUpdate = this._onGroundTrackStateUpdate.bind(this);
    this._updateGroundTracksTiming = this._updateGroundTracksTiming.bind(this);
    this._getDemCenter = this._getDemCenter.bind(this);
    this._ensureDemPlacemark = this._ensureDemPlacemark.bind(this);
    this._syncDemEntitiesToEarthOrientation =
      this._syncDemEntitiesToEarthOrientation.bind(this);
    this.showDemModel = this.showDemModel.bind(this);

    this.setTrailWidths = this.setTrailWidths.bind(this);
    this._createSpacecraftEntities = this._createSpacecraftEntities.bind(this);
  }

  init() {
    // Subscribe to global state changes.
    this._subscribeToStates();

    //new label quad tree for weighting labels for display
    this._labelQuadtree = new LabelQuadtree(pioneer.getViewportAt(0));

    Entity.createGroup("stars", this._scene, {
      milkyWaySprite: true,
      skybox: false,
      starfield: true,
      skyboxResolution: 8192,
    });

    let that = this;
    Object.keys(ENTITY_INFO).map((v, i) => {
      const p = ENTITY_INFO[v];

      if (
        p.stellar &&
        p.id != "sun" &&
        p.id != "134340_pluto" &&
        !p.isMoon &&
        p.id != "earth"
      ) {
        that.createPlanetEntity(p);
      } else if (p.isMoon) {
        that.createEntityWithLabel(p.id);
      }
    });

    //For Pluto all of these need to get made, afaict pluto relies on the barycenter, and the barycenter has references to charon so those need to get made also...
    this.createEntityWithLabel("134340_pluto_barycenter");

    this.createEntityWithLabel("134340_pluto");

    //   // Create earth.
    const originalEarthOptions = Entity.getEntityOptions("earth");
    const earthOptions = JSON.parse(JSON.stringify(originalEarthOptions));
    // Preventing all the default textures speeds up load time a lot.
    earthOptions.spheroidLOD.textures = false;

    // Recopy the postCreateFunction as it doesnt get copied correctly by JSON.parse.
    earthOptions.postCreateFunction = originalEarthOptions.postCreateFunction;
    earthOptions["label"] = ar["earth"];
    const earth = Entity.createFromOptions("earth", earthOptions, this._scene);
    this._labelQuadtree.addEntity(earth, this.getWeightForEntity("earth"));

    // Disable the atmosphere as we dont use it in this app.
    earth.getComponent(Pioneer.AtmosphereComponent).setEnabled(false);
    // Create the Pick controller for Earth
    earth.addController(Pioneer.PickController, "dataReader");
    // Create the patch component and make sure it's disabled.
    const patchComponent = earth.addComponent(
      Pioneer.SpheroidLODComponent,
      "patch",
    );

    patchComponent.setForceLoaded(true);
    patchComponent.setEnabled(false);

    // Force load the earth spheroid.
    const earthSpheroidComponent = earth.getComponent(
      Pioneer.SpheroidLODComponent,
    );
    earthSpheroidComponent.setForceLoaded(true);

    const moon = Entity.create("moon", this._scene);

    const moonSpheroid = moon.get("spheroidLOD");
    //moonSpheroid.setTexture('color', moonSpheroid.getTextureUrl('color'), [4, 16]);
    // moonSpheroid.setTexture('normal', moonSpheroid.getTextureUrl('normal'), [4]);

    // Create the patch spheroid and set it as the reference for the patch component.
    const patchSpheroid = earth.addComponent(
      Pioneer.SpheroidComponent,
      "patchSpheroid",
    );
    const earthSpheroid = earth.getComponent(Pioneer.SpheroidComponent, 0);
    patchSpheroid.setPlanetographic(true);
    patchSpheroid.setEquatorialRadius(earthSpheroid._equatorialRadius + 5);
    patchSpheroid.setPolarRadius(earthSpheroid._polarRadius + 5);
    patchComponent.setSpheroidReference("patchSpheroid");

    Entity.register({
      sc_saudisat_4: {
        groups: ["earth", "spacecraft"],
        clickable: true,
        occlusionRadius: 0.00198,
        extentsRadius: 0.0015,
        label: ar["sc_saudisat_4"],
        parents: [[23030.92908476, "earth"]],
        trail: {
          length: 5653.2,
        },
        model: {
          url: "assets/ce2s2_models/SudiSatup4_12.glb",
          shadowEntities: ["earth", "moon"],
          scale: [0.0002, 0.0002, 0.0002],
          useCompressedTextures: false,
        },
        controllers: [
          {
            type: "orbitalElements",
            name: "sc_saudisat_4",
            epoch: 0,
            eccentricity: 0.0048577,
            semiMajorAxis: 7924.41,
            meanAngularMotion: (14.7630988 * 2 * Math.PI) / 3600 / 24,
            meanAnomalyAtEpoch: 255.4208 * (Math.PI / 180),
            inclination: 97.6455 * (Math.PI / 180),
            argumentOfPeriapsis: 105.2381 * (Math.PI / 180),
            longitudeOfAscendingNode: 178.1483 * (Math.PI / 180),
          },
          {
            type: "align",
            primary: {
              type: "point",
              target: "earth",
              axis: Pioneer.Vector3.YAxis,
            },
          },
        ],
      },
    });

    // Make the label quadtree update every frame.
    pioneer.addCallback(() => {
      this._labelQuadtree.update();
    }, true);

    // Once we've loaded the first earth textures, we can then initialize the remaining scene.
    const initRemainingScene = () => {
      //deal with sun created differently from eyes on earth
      const sun = this._scene.getEntity("sun");
      this._labelQuadtree.addEntity(sun, this.getWeightForEntity("sun"));
      const sunLabel = sun.getComponent(Pioneer.DivComponent).getDiv();
      const arSun = ar["sun"];
      sunLabel.innerHTML = `<span class="icon"></span><span class="text">${arSun}</span>`;

      // Create city placemarks.
      this.createCityPlacemarks();
      this.createregionsPlacemarks();

      // Create celestials
      Entity.create("observable_universe", this._scene);
      Entity.create("milky_way", this._scene);

      const earth = this._scene.getEntity("earth");
      const earthSpheroidComponent = earth.getComponent(
        Pioneer.SpheroidComponent,
      );
      earthSpheroidComponent.setResourcesLoadedCallback(() => {
        this.createEarthquakesPlacemarks();
      });

      //this.createDemModel();

      // Create moon
      const moon = Entity.create("moon", this._scene);
      // const moonSpheroid = moon.get('spheroidLOD');
      // moonSpheroid.setTexture('color', moonSpheroid.getTextureUrl('color'), [4]);
      // moonSpheroid.unsetTexture('normal'); // Normals not necessary from this distance.

      // Setup compare objects
      const scientist = Entity.create("scientist", this._scene);
      scientist.setEnabled(false);
      const bus = Entity.create("school_bus", this._scene);
      bus.setEnabled(false);

      Entity.register({
        astronaut: {
          groups: ["comparison"],
          radius: 0.000835,
          label: "Saudi Astronaut",
          parents: [],
          model: {
            url: "assets/ce2s2_models/astronaut/ast_without_face.glb",
            rotate: [{ x: 90 }],
          },
          controllers: [
            {
              type: "fixed",
              position: Pioneer.Vector3.Zero,
              orientation: Pioneer.Quaternion.Identity,
            },
          ],
        },
      });
      const astronaut = Entity.create("astronaut", this._scene);
      astronaut.setEnabled(false);

      Entity.register({
        camel: {
          groups: ["comparison"],
          radius: 0.000835,
          label: "Arabic Camel",
          parents: [],
          model: {
            url: "assets/ce2s2_models/camel_May_12.glb",
            rotate: [{ x: 90 }, { z: 90 }],
            useCompressedTextures: false,
          },
          controllers: [
            {
              type: "fixed",
              position: Pioneer.Vector3.Zero,
              orientation: Pioneer.Quaternion.Identity,
            },
          ],
        },
      });
      const Camel = Entity.create("camel", this._scene);
      Camel.setEnabled(false);

      const createSpacecraft = (spacecraftList) => {
        this._createSpacecraftEntities(spacecraftList);
        const { getManager } = globalRefs;
        getManager("label").buildLabels();

        // Set spacecraftLoaded to true.
        spacecraftStore.setGlobalState({ spacecraftLoaded: true });
      };
      // Check if we have a spacecraft list yet (it's almost certain to be there)
      const { spacecraftList } = spacecraftStore.stateSnapshot;
      // If so, create them.
      if (spacecraftList) {
        createSpacecraft(spacecraftList);
      } else {
        // If not, subscribe to spacecraftList state, so they'll be created when it's available.
        const unsubscribeToSpacecraftList = spacecraftStore.subscribeTo(
          "spacecraftList",
          (spacecraftList) => {
            createSpacecraft(spacecraftList);
            unsubscribeToSpacecraftList();
          },
        );
      }
    };

    /**
     * On first load, we need to wait until the initial textures for the earth have loaded.
     * Once the promise has resolved, we can initRemainingScene and unsubscribe from the isTextureLoaded.
     */
    const unsubscribeToSplashScreen = uiStore.subscribeTo(
      "showSplashScreen",
      (showSplashScreen) => {
        if (!showSplashScreen) {
          // Init the rest of the scene.
          initRemainingScene();
          // Unsubscribe from isLoaded so this is not called again.
          unsubscribeToSplashScreen();
        }
      },
    );
  }

  setTrailWidths(width = this._trailWidth) {
    this._trailWidth = width;

    // Return if we dont yet have spacecraft entities (ie. spacecraft list not yet loaded).
    if (!this._operatingSC) {
      return;
    }

    this._operatingSC.forEach((sc) => {
      const trailComponent = this._scene.get(sc, "trail");
      trailComponent?.setWidths(0, this._trailWidth);
    });
  }

  setTrailWidths(width = this._trailWidth) {
    this._trailWidth = width;

    // Return if we dont yet have spacecraft entities (ie. spacecraft list not yet loaded).
    if (!this._operatingSC) {
      return;
    }

    this._operatingSC.forEach((sc) => {
      try {
        const trailComponent = this._scene
          .getEntity(sc)
          .getComponent(Pioneer.TrailComponent);
        trailComponent?.setWidths(0, this._trailWidth);
      } catch (e) {
        console.error("setTrailWidths", e, sc);
      }
    });
  }

  getWeightForEntity(name) {
    const bigguns = [
      "mercury",
      "venus",
      "earth",
      "mars",
      "saturn",
      "jupiter",
      "neptune",
      "uranus",
      "134340_pluto",
    ];
    let weight = 1;
    if (name == "sun") {
      weight = 5;
    } else if (bigguns.includes(name)) {
      weight = 3;
    } else if (name == "moon") {
      weight = 2;
    }

    return weight;
  }

  /**
   * Attach callbacks to global states.
   *
   * Note: as the scene manager is never unmounted, we do not need to unsubscribe.
   */
  _subscribeToStates() {
    // Subscribe to enabledSC state.
    spacecraftStore.subscribeTo("enabledSC", this.setEnabledSC.bind(this));

    // Subscribe to events state.
    eventsStore.subscribeAll(this._onEventsStateUpdate);
    poiStore.subscribeAll(this._onPoIStateUpdate);
    gmapStore.subscribeAll(this._onGmapStateUpdate);
    // Subscribe to light state.
    lightStore.subscribeTo("fill", this._onLightStateUpdate);

    // Subscribe to city names and ground tracks state.
    layersStore.subscribeTo("cityNames", this._onCityNamesStateUpdate);
    layersStore.subscribeTo("regions", this._onregionsStateUpdate);
    layersStore.subscribeTo("earthquakes", this.startEarthquakeTimeline);
    layersStore.subscribeTo("dem", this._onDemStateUpdate);
    layersStore.subscribeTo("demModelId", this._onDemModelIdStateUpdate);
    layersStore.subscribeTo("groundTracks", this._onGroundTrackStateUpdate);
    layersStore.subscribeTo(
      "groundTrackStartTime",
      this._updateGroundTracksTiming,
    );
    // Subscribe to texture loading state (show texture loading is done manually)
    datasetStore.subscribeTo(
      "isTextureLoaded",
      (isTextureLoaded) => isTextureLoaded && hideLoading("texture"),
    );

    // Subscribe to trailWidth query.
    appStore.subscribeTo("queries", (queries) => {
      queries?.trailWidth && this.setTrailWidths(queries.trailWidth);
    });
  }

  createPlanetEntity(planet) {
    const p = this.createEntityWithLabel(planet.id);
    const planetSpheroid = p.getComponent(Pioneer.SpheroidLODComponent);
    const texture_arr = ["neptune", "uranus"].includes(planet.id) ? 256 : 512;
    planetSpheroid.setTexture("color", planetSpheroid.getTextureUrl("color"), [
      texture_arr,
    ]);
  }
  /**
   * Create spacecraft entities.
   */
  _createSpacecraftEntities(spacecraftList) {
    // Build operating and past and future spacecraft arrays.
    this._operatingSC = [];
    this._nonOperatingSC = [];

    Object.entries(spacecraftList).forEach(([key, { operating_status }]) => {
      if (operating_status === "Current") {
        this._operatingSC.push(key);
      } else if (operating_status === "Past" || operating_status === "Future") {
        this._nonOperatingSC.push(key);
      }
    });

    // Make sure SCs are unique.
    this._operatingSC = [...new Set(this._operatingSC)];
    this._nonOperatingSC = [...new Set(this._nonOperatingSC)];

    // Create the entities.
    this._operatingSC.forEach((sc) => this.createEntityWithLabel(sc));
    this._nonOperatingSC.forEach((sc) => this.createEntityWithLabel(sc));

    // Set the trail widths.
    this.setTrailWidths();

    // Set the trail colors.
    const setTrailColors = (sc) => {
      const trailComponent = this._scene
        .getEntity(sc)
        .getComponent(Pioneer.TrailComponent);
      const result = spacecraftList[sc].categories
        .filter((type) =>
          ["ATMOSPHERE", "LAND", "SEA"].includes(type.toUpperCase()),
        )
        .map((type) => TRAIL_COLORS[type.toUpperCase()]);
      if (!result) {
        console.warn(
          `Could not find color for spacecraft ${sc}`,
          result,
          spacecraftList[sc].categories,
        );
        return;
      }
      trailComponent?.setColor(new Pioneer.Color(...result[0]));
    };
    this._operatingSC.forEach(setTrailColors);
    this._nonOperatingSC.forEach(setTrailColors);

    // #1595 - These sc_iss lines can be removed at a later date when camera orientations for spacecraft children is implemented)
    this._scene
      .getEntity("sc_iss_oco_3")
      .getController(Pioneer.FixedController)
      .setOrientation(
        new Pioneer.Quaternion(Math.sqrt(0.5), -Math.sqrt(0.5), 0, 0),
      );
    this._scene
      .getEntity("sc_iss_oco_3")
      .getComponent(Pioneer.DivComponent)
      .setVisibleInterval(new Pioneer.VisibleInterval(0, 5, "z-distance"));
    this._scene.getEntity("sc_iss_oco_3").setCanOcclude(false);

    this._scene
      .getEntity("sc_iss_ecostress")
      .getController(Pioneer.FixedController)
      .setOrientation(
        new Pioneer.Quaternion(Math.sqrt(0.5), -Math.sqrt(0.5), 0, 0),
      );
    this._scene
      .getEntity("sc_iss_ecostress")
      .getComponent(Pioneer.DivComponent)
      .setVisibleInterval(new Pioneer.VisibleInterval(0, 5, "z-distance"));
    this._scene.getEntity("sc_iss_ecostress").setCanOcclude(false);

    this._scene
      .getEntity("sc_iss_emit")
      .getComponent(Pioneer.DivComponent)
      .setVisibleInterval(Pioneer.VisibleInterval.AlwaysVisible);
    this._scene
      .getEntity("sc_iss_emit")
      .getComponent(Pioneer.DivComponent)
      .setVisibleInterval(new Pioneer.VisibleInterval(0, 5, "z-distance"));
    this._scene.getEntity("sc_iss_emit").setCanOcclude(false);

    this._scene
      .getEntity("sc_iss_cowvr_tempest")
      .getController(Pioneer.FixedController)
      .setOrientation(
        new Pioneer.Quaternion(Math.sqrt(0.5), -Math.sqrt(0.5), 0, 0),
      );
    this._scene
      .getEntity("sc_iss_cowvr_tempest")
      .getComponent(Pioneer.DivComponent)
      .setVisibleInterval(new Pioneer.VisibleInterval(0, 5, "z-distance"));
    this._scene.getEntity("sc_iss_cowvr_tempest").setCanOcclude(false);

    // Check operating spacecraft to make sure they're in coverage.
    // Wait until entities have downloaded dynamo coverage data to check.
    this._scene
      .waitTillEntitiesInPlace(this._operatingSC)
      .then(() => {
        this._operatingSC = this._operatingSC.filter((spacecraftId) => {
          const spacecraftEntity = this._scene.getEntity(spacecraftId);
          const dynamoController = spacecraftEntity.getController(
            Pioneer.DynamoController,
          );
          const orbitalElements = spacecraftEntity.getController(
            Pioneer.OrbitalElementsController,
          );
          // If there's no dynamo controller, it's probably an instrument.
          if (!dynamoController && !orbitalElements) {
            return true;
          }

          const isInDynamoCoverage = dynamoController
            .getCoverage()
            .contains(this._pioneer.getTime());
          const isInOrbitalElementsCoverage = orbitalElements
            ?.getCoverage()
            .contains(this._pioneer.getTime());
          // If the spacecraft is not in coverage, it shouldn't be in this list - destroy it.
          if (!isInDynamoCoverage && !isInOrbitalElementsCoverage) {
            spacecraftEntity.__destroy();
          }
          return isInDynamoCoverage || isInOrbitalElementsCoverage;
        });
      })
      .catch(() => {});

    this._nonOperatingSC.forEach((sc) => {
      const entity = this._scene.getEntity(sc);
      entity.clearParentingTableEntries();
      entity.addParentingTableEntry(Number.NEGATIVE_INFINITY, "earth");
      const oeController = entity.getController(
        Pioneer.OrbitalElementsController,
      );

      if (oeController !== null) {
        // Assumes future s/c have orbital element controllers
        oeController.setCoverage(
          new Pioneer.Interval(
            Number.NEGATIVE_INFINITY,
            Number.POSITIVE_INFINITY,
          ),
        );
      } else {
        const dynamoController =
          entity.getController(Pioneer.DynamoController) ?? undefined;
        const oeController = entity.addController(
          Pioneer.OrbitalElementsController,
          undefined,
          dynamoController,
        );
        const oe = new Pioneer.OrbitalElements();
        oe.epoch = this._scene.getEngine().getTime();
        // Use NISAR data for any s/c that doesn't have oeController
        oe.eccentricity = 0;
        oe.semiMajorAxis = 7118;
        oe.meanAngularMotion = 0.0011382582;
        oe.meanAnomalyAtEpoch = 0;
        oe.setOrientationFromElements(
          (Math.PI / 180) * (Math.random() * 180 - 90),
          0,
          0,
        );
        oeController.addOrbitalElements(oe.epoch, oe);
        oeController.setCoverage(
          new Pioneer.Interval(
            Number.NEGATIVE_INFINITY,
            Number.POSITIVE_INFINITY,
          ),
        );
      }
    });

    if (this._enabledSC !== null) {
      this.setEnabledSC(this._enabledSC);
    }
  }

  createEntityWithLabel(sc) {
    const baseOptions = Entity.getEntityOptions(sc);
    const options = baseOptions ? { ...baseOptions } : null;
    if (options) {
      const ar_label = ar[sc];
      if (ar_label) {
        options["label"] = ar_label;
      } else {
        console.log("FIX: No translation in ar.js for ", sc);
      }
      const plutoSecondaryBodies = [
        "charon",
        "kerberos",
        "styx",
        "hydra",
        "nix",
      ];
      if (plutoSecondaryBodies.includes(sc)) {
        options["labelFadeEntity"] = "134340_pluto";
      }
      let _e;
      if (sc != "earth") {
        _e = Entity.createFromOptions(sc, options, this._scene);
        this._labelQuadtree.addEntity(_e, this.getWeightForEntity(sc));
      }

      if (sc == "134340_pluto_barycenter") {
        let entity = this._scene.get(sc);
        const entityEl = entity.getComponent(Pioneer.DivComponent);
        const div = entityEl.getDiv();
        div.classList.remove("pioneer-label-div");
      }

      return _e;
    } else {
      console.error("Don't know how to create: ", sc);
      return null;
    }
  }

  /**
   * Handles a change of the enabledSC global state.
   * @param {Array<string>|string|Boolean} ids
   */
  setEnabledSC(ids) {
    // Check whether we have booleans.
    const showAllOperating = ids === "operating";
    const showAll = ids === true;

    /**
     * It's possible we dont yet have the spacecraft entities built.
     * In that case, We need to set a local variable to be used upon their creation.
     */
    if (!this._operatingSC) {
      this._enabledSC = ids;
      return;
    }

    this._operatingSC?.forEach((spacecraftId) => {
      const spacecraftEntity = this._scene.getEntity(spacecraftId);

      if (spacecraftEntity) {
        spacecraftEntity.setEnabled(
          (Array.isArray(ids) && ids.includes(spacecraftId)) ||
            showAllOperating ||
            showAll ||
            this._scene.getEntity("camera").getParent()?.getName() ===
              spacecraftId,
        );
      }
    });

    this._nonOperatingSC?.forEach((spacecraftId) => {
      // console.log("nonOperatingSC", spacecraftId, ids)
      const spacecraftEntity = this._scene.getEntity(spacecraftId);

      if (spacecraftEntity) {
        //hide all non-operating spacecrafts.
        spacecraftEntity.setEnabled(
          this._scene.getEntity("camera").getParent()?.getName() ===
            spacecraftId,
        );
      }
    });
  }

  /**
   * Returns whether the spacecraft is an operating spacecraft
   * @param {string} spacecraftId
   * @returns {boolean} Whether the spacecraft is a operating spacecraft
   */
  isOperatingSc(spacecraftId) {
    return this._operatingSC?.includes(spacecraftId);
  }

  /**
   * Handles a change of the light global state
   * @param {Boolean} newState
   */
  _onLightStateUpdate(newState) {
    const camera = this._scene.getEntity("camera");
    const parent = camera?.getParent();
    const earth = this._scene.getEntity("earth");

    // return if camera and earth are not loaded
    if (!parent || !earth) return;

    const name = parent.getName();

    const { spacecraftList } = spacecraftStore.stateSnapshot;

    // turn camera light off if looking at the earth
    this.setCameraLighting(false);

    // if camera is looking at a spacecraft, use camera lighting instead of ambient light color
    // because ambient color makes portions of the spacecraft to have identical colors
    Object.keys(spacecraftList).map((spacecraftId) => {
      if (spacecraftId === name) {
        this.setCameraLighting(newState);
        return;
      }
    });

    this.setEarthLighting(newState);
  }

  /**
   * Handles a change of the city names global state
   * @param {Boolean} newState
   */
  _onCityNamesStateUpdate(newState) {
    const placemarks = this._scene.getEntity("placemarks");
    if (!placemarks || !placemarks.getNumChildren()) {
      return;
    }

    for (let i = 0; i < placemarks.getNumChildren(); i++) {
      placemarks.getChild(i).setEnabled(newState);
    }
  }

  _onregionsStateUpdate(newState) {
    const placemarks = this._scene.get("Regionplacemarks");

    if (!placemarks || !placemarks.getNumChildren()) {
      return;
    }
    for (let i = 0; i < placemarks.getNumChildren(); i++) {
      placemarks.getChild(i).setEnabled(newState);
    }
  }

  _onEarthquakesStateUpdate() {
    console.log("_onEarthquakesStateUpdate");
    // const { earthquakes } = layersStore.stateSnapshot;
    // const placemarks = this._scene.get('Earthquakesplacemarks');
    // if (!placemarks) return;

    // for (let i = 0; i < placemarks.getNumChildren(); i++) {
    //   const entity = placemarks.getChild(i);

    //   const visible =
    //     earthquakes &&
    //     this.currentVisibleTime !== null &&
    //     entity._timeValue !== undefined &&
    //     entity._timeValue <= this.currentVisibleTime;

    //   entity.setEnabled(visible);
    // }
  }

  async startEarthquakeTimeline() {
    const { earthquakes } = layersStore.stateSnapshot;
    uiStore.setGlobalState({ controlsMenu: false });
    lightStore.setGlobalState({ fill: true });
    const { getManager } = globalRefs;
    if (earthquakes) {
      //video is not aligning with the video map... can't seem to find a better projection
      // The process is to see what the python reports as the min/max then tweak it manually by a few degrees below
      const west_tweak = 24.9;
      const east_tweak = 22.4;
      const north_tweak = 6.1;
      const south_tweak = 5.3;
      const videoData = {
        boundaries: {
          south: 2.55188 - south_tweak,
          north: 40.86572 + north_tweak,
          west: 21.71417 - west_tweak,
          east: 73.38773 + east_tweak,
        },
        type: "geoLocated",
        transparent: true,
        scrubber: true,
        src: "https://ce2s2.caltech.edu/assets/earthquake_animation.webm",
      };
      // videoStore.setGlobalState({ isVideoTextureLoaded: true });
      await getManager("camera")._goToEarth({ latitude: 20, longitude: 50 });
      getManager("camera").setSpinRate(0);
      getManager("video")
        .loadVideo(videoData)
        .then(() => {
          videoStore.setGlobalState({
            isVideoTextureLoaded: true,
            isVideoPlaying: true,
          });
        });
    } else {
      getManager("video").unloadVideo();
      const patchComponent = pioneer
        .getScene("main")
        .getEntity("earth")
        .getComponent(Pioneer.SpheroidLODComponent, "patch");
      patchComponent.setEnabled(false);
    }

    // const { earthquakes } = layersStore.stateSnapshot;
    // if (!earthquakes) {
    //   this.pauseEarthquakeTimeline();
    //   this.currentVisibleTime = null;
    //   this._onEarthquakesStateUpdate();
    //   return;
    // }

    // const placemarks = this._scene.get('Earthquakesplacemarks');
    // if (!placemarks) return;

    // this._earthquakeTimes = [];
    // for (let i = 0; i < placemarks.getNumChildren(); i++) {
    //   const t = placemarks.getChild(i)._timeValue;
    //   if (t !== undefined) this._earthquakeTimes.push(t);
    // }

    // if (!this._earthquakeTimes.length) {
    //   this.currentVisibleTime = null;
    //   return;
    // }

    // // Sort oldest → newest
    // this._earthquakeTimes.sort((a, b) =>  a - b);
    // this._earthquakeIndex = 0;

    // this.currentVisibleTime = this._earthquakeTimes[0];
    // this._onEarthquakesStateUpdate();
    // this.playEarthquakeTimeline();
  }

  getEarthquakeTime() {
    return this.currentVisibleTime ?? null;
  }

  resetEarthquakeTimeline() {
    // if (!this._earthquakeTimes?.length) return;
    // this._earthquakeIndex = 0;
    // this.currentVisibleTime = this._earthquakeTimes[0];
    // console.log({resetTime: this.currentVisibleTime})
    // this._onEarthquakesStateUpdate();
  }

  //PLAY earthquake timeline
  playEarthquakeTimeline() {
    getManager("video").playVideo();

    // if (this._earthquakeTimer) return;
    // if (!this._earthquakeTimes?.length) return;

    // if (this._earthquakeIndex >= this._earthquakeTimes.length) {
    //   this.resetEarthquakeTimeline();
    // }

    // const DELAY = 1;

    // this._earthquakeTimer = setInterval(() => {
    //   if (this._earthquakeIndex >= this._earthquakeTimes.length) {
    //     this.pauseEarthquakeTimeline();
    //     return;
    //   }

    //   this.currentVisibleTime =
    //     this._earthquakeTimes[this._earthquakeIndex++];

    //   this._onEarthquakesStateUpdate();
    // }, DELAY);
  }

  // PAUSE

  pauseEarthquakeTimeline() {
    getManager("video").pauseVideo();
    // if (this._earthquakeTimer) {
    //   clearInterval(this._earthquakeTimer);
    //   this._earthquakeTimer = null;
    // }
  }

  // NEXT

  // nextEarthquake() {
  //   this.pauseEarthquakeTimeline();
  //   if (!this._earthquakeTimes?.length) return;

  //   this._earthquakeIndex = Math.min(
  //     this._earthquakeIndex + 1,
  //     this._earthquakeTimes.length - 1
  //   );

  //   this.currentVisibleTime =
  //     this._earthquakeTimes[this._earthquakeIndex];

  //   this._onEarthquakesStateUpdate();
  // }

  //PREVIOUS

  // previousEarthquake() {
  //   this.pauseEarthquakeTimeline();
  //   if (!this._earthquakeTimes?.length) return;

  //   this._earthquakeIndex = Math.max(this._earthquakeIndex - 1, 0);

  //   this.currentVisibleTime =
  //     this._earthquakeTimes[this._earthquakeIndex];

  //   this._onEarthquakesStateUpdate();
  // }

  _onDemStateUpdate(newState) {
    const demData = this._getSelectedDemData();
    let demEntity = this._scene.get("demModel");
    let demPlacemark = this._scene.get("demPlacemark");
    if (!demEntity && newState && demData) {
      // if toggle is flipped before initial scene build finishes.
      this.createDemModel();
      demEntity = this._scene.get("demModel");
    }
    demEntity?.setEnabled(newState);
    demPlacemark?.setEnabled(newState);
    this._applyDemLightingBoost(newState);

    // If DEM has bounds, fly camera.
    if (newState && demEntity && demData) {
      const { getManager } = globalRefs;
      const cameraManager = getManager("camera");
      demPlacemark = this._ensureDemPlacemark();
      demPlacemark?.setEnabled(true);

      const { centerLat, centerLon, bounds } = this._getDemBounds();
      const pad = 0.01; // ~1 km pad to avoid zero-area bounds
      const north = bounds?.north ?? centerLat + pad;
      const south = bounds?.south ?? centerLat - pad;
      const east = bounds?.east ?? centerLon + pad;
      const west = bounds?.west ?? centerLon - pad;

      cameraManager?.goToDataPatch(
        north,
        south,
        east,
        west,
        demPlacemark,
        demData.distance_from_poi ?? 0,
        demData.cameraFramingMultiplier ?? 0.75,
      );
    } else if (!newState) {
      const { getManager } = globalRefs;
      const route = getManager("route");
      route?.goHomeRetainURLQueries?.();
    }
  }

  _onDemModelIdStateUpdate() {
    this.createDemModel();

    if (layersStore.stateSnapshot.dem) {
      this._onDemStateUpdate(true);
    }
  }

  _getSelectedDemData() {
    const { demModelId } = layersStore.stateSnapshot;
    return getDemModelById(demModelId);
  }

  showDemModel(demModelId) {
    const { dem, demModelId: currentDemModelId } = layersStore.stateSnapshot;
    const sameModel = demModelId === currentDemModelId;
    const alreadyVisible = dem === true;

    layersStore.setGlobalState({
      dem: true,
      demModelId,
    });

    if (sameModel && alreadyVisible) {
      this.createDemModel();
      this._onDemStateUpdate(true);
    }
  }

  _getDemBounds() {
    const demData = this._getSelectedDemData();
    if (!demData) {
      return { centerLat: 0, centerLon: 0, bounds: null };
    }

    const hasBoundaries =
      typeof demData.north_boundary === "number" &&
      typeof demData.south_boundary === "number" &&
      typeof demData.east_boundary === "number" &&
      typeof demData.west_boundary === "number";

    let lat = demData.latLng?.[0];
    let lon = demData.latLng?.[1];

    if (hasBoundaries) {
      lat = (demData.north_boundary + demData.south_boundary) / 2;
      lon = (demData.east_boundary + demData.west_boundary) / 2;
    } else if (demData.center && typeof demData.center === "string") {
      const parts = demData.center.split(",").map((p) => Number(p.trim()));
      if (parts.length === 2 && parts.every((v) => !Number.isNaN(v))) {
        lat = parts[0];
        lon = parts[1];
      }
    }

    const tuning = demData.tuning || {};
    const offset = Array.isArray(tuning.offset) ? tuning.offset : [];
    const latOffset = Number(offset[0]) || 0;
    const lonOffset = Number(offset[1]) || 0;
    const centerLat = (lat ?? 0) + latOffset;
    const centerLon = (lon ?? 0) + lonOffset;

    let bounds = null;
    if (hasBoundaries) {
      let north = demData.north_boundary + latOffset;
      let south = demData.south_boundary + latOffset;
      let east = demData.east_boundary + lonOffset;
      let west = demData.west_boundary + lonOffset;

      const boundsScale =
        typeof tuning.boundsScale === "number" ? tuning.boundsScale : 1;
      if (boundsScale !== 1) {
        const halfLat = (north - south) / 2;
        const halfLon = (east - west) / 2;
        north = centerLat + halfLat * boundsScale;
        south = centerLat - halfLat * boundsScale;
        east = centerLon + halfLon * boundsScale;
        west = centerLon - halfLon * boundsScale;
      }

      bounds = { north, south, east, west };
    }

    return { centerLat, centerLon, bounds };
  }

  _getDemCenter() {
    const { centerLat, centerLon } = this._getDemBounds();
    return { lat: centerLat, lon: centerLon };
  }

  _multiplyQuaternionsWXYZ(a, b) {
    if (!a || !b) {
      return a || b;
    }

    const aw = a.w;
    const ax = a.x;
    const ay = a.y;
    const az = a.z;
    const bw = b.w;
    const bx = b.x;
    const by = b.y;
    const bz = b.z;

    if (
      typeof aw !== "number" ||
      typeof ax !== "number" ||
      typeof ay !== "number" ||
      typeof az !== "number" ||
      typeof bw !== "number" ||
      typeof bx !== "number" ||
      typeof by !== "number" ||
      typeof bz !== "number"
    ) {
      return a;
    }

    return new Pioneer.Quaternion(
      aw * bw - ax * bx - ay * by - az * bz,
      aw * bx + ax * bw + ay * bz - az * by,
      aw * by - ax * bz + ay * bw + az * bx,
      aw * bz + ax * by - ay * bx + az * bw,
    );
  }

  _ensureDemTransformSync() {
    if (this._demTransformSyncInstalled) {
      return;
    }

    this._pioneer.addCallback(this._syncDemEntitiesToEarthOrientation, true);
    this._demTransformSyncInstalled = true;
  }

  _applyEarthFixedEntityTransform(
    entity,
    earthFixedPosition,
    earthFixedOrientation,
  ) {
    const earth = this._scene.get("earth");
    const earthOrientation = earth?.getOrientation?.();
    if (
      !entity ||
      !earthFixedPosition ||
      !earthFixedOrientation ||
      !earthOrientation
    ) {
      return;
    }

    const rotatedPosition = new Pioneer.Vector3();
    rotatedPosition.rotate(earthOrientation, earthFixedPosition);
    entity.setPosition(rotatedPosition);

    const rotatedOrientation = new Pioneer.Quaternion();
    rotatedOrientation.mult(earthOrientation, earthFixedOrientation);
    entity.setOrientation(rotatedOrientation);
  }

  _syncDemEntitiesToEarthOrientation() {
    const demEntity = this._scene.get("demModel");
    if (
      demEntity &&
      this._demModelLocalPosition &&
      this._demModelLocalOrientation
    ) {
      this._applyEarthFixedEntityTransform(
        demEntity,
        this._demModelLocalPosition,
        this._demModelLocalOrientation,
      );
    }

    const placemarkEntity = this._scene.get("demPlacemark");
    if (
      placemarkEntity &&
      this._demPlacemarkLocalPosition &&
      this._demPlacemarkLocalOrientation
    ) {
      this._applyEarthFixedEntityTransform(
        placemarkEntity,
        this._demPlacemarkLocalPosition,
        this._demPlacemarkLocalOrientation,
      );
    }
  }

  _ensureDemPlacemark() {
    const earth = this._scene.get("earth");
    const spheroid = earth.getComponent(Pioneer.SpheroidComponent);
    let placemarkEntity = this._scene.get("demPlacemark");
    if (!placemarkEntity && spheroid) {
      placemarkEntity = this._scene.addEntity("demPlacemark");
      placemarkEntity.setParent(earth);
      placemarkEntity.setCanOcclude(false);
      placemarkEntity.setExtentsRadius(0.001);
      const div = placemarkEntity.addComponent(Pioneer.DivComponent);
      div.setEnabled(true);
      const d = div.getDiv();
      d.classList.add("placemark");
      d.style.background = "rgba(255, 255, 255, 0)";
      d.style.width = "12px";
      d.style.height = "12px";
      d.style.borderRadius = "50%";
      d.style.border = "1px solid #fff";
      d.style.pointerEvents = "none";
      d.style.transform = "translate(-50%, -50%)";
    }

    if (placemarkEntity && spheroid) {
      const { lat, lon } = this._getDemCenter();
      const lla = new Pioneer.LatLonAlt(
        Pioneer.MathUtils.degToRad(lat || 0),
        Pioneer.MathUtils.degToRad(lon || 0),
        0,
      );
      const position = new Pioneer.Vector3();
      const orientation = new Pioneer.Quaternion();

      spheroid.xyzFromLLA(position, lla);
      spheroid.orientationFromLLA(orientation, lla);

      this._demPlacemarkLocalPosition = new Pioneer.Vector3(
        position.x,
        position.y,
        position.z,
      );
      this._demPlacemarkLocalOrientation = new Pioneer.Quaternion(
        orientation.w,
        orientation.x,
        orientation.y,
        orientation.z,
      );
      this._ensureDemTransformSync();
      this._syncDemEntitiesToEarthOrientation();
    }

    return placemarkEntity;
  }

  /**
   * Handles a change of the ground tracks global state
   * @param {Boolean} newState
   */
  _onGroundTrackStateUpdate(newState) {
    const { getManager } = globalRefs;
    const { spacecraftId: currentSpacecraftId } = spacecraftStore.stateSnapshot;
    const { currentDataset } = datasetStore.stateSnapshot;
    const _8_hours = 28800;
    const _24_hours = 86400;
    const originalStartTime = this._pioneer.getTime() - _8_hours;

    if (!newState) {
      this.groundTracksEntity
        .getController(Pioneer.CoverageController)
        ?.setEnabled(false);
    }

    // Create child entity for spacecraft to act as ground tracks
    let hasGroundTracksEntity = false;
    // If on a vital sign, use the spacecraft from mission ids, of if on spacecraft view, use spacecraftId
    const spacecraftId = currentSpacecraftId || currentDataset?.missionIds?.[0];
    const spacecraft = this._scene.getEntity(spacecraftId);

    if (newState && spacecraft) {
      // Set ground tracks entity to be the appropriate child of the spacecraft
      for (let i = 0; i < spacecraft.getNumChildren(); i++) {
        if (
          spacecraft.getChild(i).getName() ===
          `groundTracksEntity_${spacecraftId}`
        ) {
          hasGroundTracksEntity = true;
          this.groundTracksEntity = spacecraft.getChild(i);
        }
      }

      if (!hasGroundTracksEntity) {
        this.groundTracksEntity = this._scene.addEntity(
          `groundTracksEntity_${spacecraftId}`,
        );
      }

      // Configure ground tracks entity
      this.groundTracksEntity.setParent(spacecraft);
      const fixedController = this.groundTracksEntity.addController(
        Pioneer.FixedController,
      );
      fixedController.setPosition(Pioneer.Vector3.Zero);
      fixedController.setOrientation(Pioneer.Quaternion.Identity);

      // Clamp ground tracks entity to the ground to clamp trail to ground
      const groundClampController = this.groundTracksEntity.addController(
        Pioneer.GroundClampController,
      );
      groundClampController.setGroundEntityRef("earth");

      const groundTracks = this.groundTracksEntity.addComponent(
        Pioneer.TrailComponent,
      );
      groundTracks.setColor(new Pioneer.Color(1, 1, 1, 1));
      groundTracks.setWidths(1, 3);

      // Fade trail as it reaches its endpoint
      groundTracks.setAlphaFade(0);
      groundTracks.setRelativeToEntity("earth");
      groundTracks.setRelativeToEntityOrientation(true);
      groundTracks.setVisibleInterval(Pioneer.VisibleInterval.AlwaysVisible);

      groundTracks.setRelativeStartTime(false);
      groundTracks.setStartTime(originalStartTime);

      groundTracks.setResourcesLoadedCallback(() => {
        const threeObject = groundTracks.getThreeJsObjects() || [];
        Pioneer.ThreeJsHelper.setRenderOrder(threeObject[0], 1.0);
      });

      const coverageController = this.groundTracksEntity.addController(
        Pioneer.CoverageController,
      );
      coverageController.setUpdateFunction((entity) => {
        // Check for tracks in case something deleted them.
        const tracks = entity.getComponent(Pioneer.TrailComponent);
        if (tracks === null) {
          return;
        }

        // Ground tracks are at 24 hours - show track lines all around the globe
        if (this._pioneer.getTime() - originalStartTime >= _24_hours) {
          groundTracks.setStartTime(this._pioneer.getTime() - _24_hours);
        } else {
          // Set ground tracks back to 8 hours behind
          if (getManager("time").getTimeRate() < 0) {
            groundTracks.setStartTime(this._pioneer.getTime() - _8_hours);
          } else {
            groundTracks.setStartTime(originalStartTime);
          }
        }
      });
    }

    this.groundTracksEntity.setEnabled(newState);
  }

  /**
   * Ensure the ground track start time is accurate to 'now'
   * @param {number} time
   */
  _updateGroundTracksTiming(time) {
    const { spacecraftId: currentSpacecraftId } = spacecraftStore.stateSnapshot;
    const groundTracks = this._scene
      .getEntity(`groundTracksEntity_${currentSpacecraftId}`)
      ?.getComponent(Pioneer.TrailComponent);
    const etTime = Pioneer.TimeUtils.unixToEt(time.valueOf() * 0.001);
    const _8_hours = 28800;

    groundTracks && groundTracks.setStartTime(etTime - _8_hours);
  }

  /**
   * Handles a change of events global state.
   *
   * Events shown on the globe must always be filtered (otherwise there's too many) to prevent UI slowdown.
   * @param {object} newState
   */
  _onEventsStateUpdate(newState) {
    const {
      categoryIndex,
      eventsVisibleOnGlobe,
      showLatest,
      events,
      yearIndex,
      eventYears,
      currentEvent,
      mediaIndex,
    } = newState;
    // Always turn off all events first.
    this.displayEventPlacemarks(false);

    if (eventsVisibleOnGlobe && eventYears) {
      // Filter events.
      const eventsYear = eventYears?.[yearIndex] || eventYears?.[0];
      const allEventsFromYear = events?.[eventsYear];

      if (Array.isArray(allEventsFromYear)) {
        const filteredEvents = filterEvents(
          allEventsFromYear,
          categoryIndex,
          mediaIndex,
        );

        this.displayEventPlacemarks(true, filteredEvents);
      }
    } else if (showLatest) {
      // Make sure we filter out the current event.
      const latestEvents = getLimitedLatestEvents().filter(
        (event) => event.id !== currentEvent?.id && event.id !== "sand_1",
      );
      latestEvents && this.displayEventPlacemarks(true, latestEvents);
    }
  }

  /**
   * Handles a change of poi global state.
   *
   * PoI shown on the globe must always be filtered (otherwise there's too many) to prevent UI slowdown.
   * @param {object} newState
   */
  _onPoIStateUpdate(newState) {
    const {
      yearIndex,
      categoryIndex,
      showOnGlobePoI,
      showLatestPoI,
      pois,
      poiYears,
    } = newState;
    // console.log("_onPoIStateUpdate", showOnGlobePoI, showLatestPoI)
    // Always turn off all poi first.
    this.displayCustomPlacemarks(false, true);
    if (showOnGlobePoI) {
      const { idPrefix } = this._options.poiPlacemarks;
      const filteredPoI = filterPoI(pois, poiYears, yearIndex, categoryIndex);
      const filteredPoIIds = filteredPoI.map((poi) => `poi_${poi.id}`);

      let showPoITitle = true;
      if (this._router?._history?.location?.pathname) {
        const { pathname } = this._router._history.location;
        showPoITitle =
          pathname.indexOf("poi") >= 0 || pathname.indexOf("spacecraft") >= 0;
      }

      this.displayCustomPlacemarks(!showPoITitle, true, filteredPoIIds);
    } else if (showLatestPoI) {
      const poiList = getLimitedPois();
      this.displayCustomPlacemarks(true, true, poiList);
    }
  }

  /**
   * Handles a change of gmap global state.
   *
   * Gmap shown on the globe must always be filtered (otherwise there's too many) to prevent UI slowdown.
   * @param {object} newState
   */
  _onGmapStateUpdate(newState) {
    const {
      yearIndex,
      categoryIndex,
      showOnGlobeGmap,
      showLatestGmap,
      gmaps,
      gmap_cats,
      gmapYears,
    } = newState;
    // Always turn off all gmap first.
    this.displayCustomPlacemarks(false);
    if (showOnGlobeGmap) {
      const { idPrefix } = this._options.gmapPlacemarks;
      const filteredGmap = filterGmap(
        gmaps,
        gmapYears,
        yearIndex,
        categoryIndex,
      );
      const filteredGmapIds = filteredGmap.map((gmap) => `gmap_${gmap.id}`);

      let showGmapTitle = true;
      if (this._router?._history?.location?.pathname) {
        const { pathname } = this._router._history.location;
        showGmapTitle =
          pathname.indexOf("gmap") >= 0 || pathname.indexOf("spacecraft") >= 0;
      }

      this.displayCustomPlacemarks(!showGmapTitle, false, filteredGmapIds);
    } else if (showLatestGmap) {
      //This doesn't currently do much since all the GMAPs have the same center placemarks are just overlapping
      const gmapList = getLimitedGmaps();
      this.displayCustomPlacemarks(true, false, gmapList);
    }
  }

  createCityPlacemarks() {
    const { cityNames } = layersStore.stateSnapshot;
    const earth = this._scene.getEntity("earth");
    const placemarks = this._scene.get("placemarks");
    if (!placemarks) {
      const group = this._scene.addEntity("placemarks");
      group.setParent(earth);
      group.setCanOcclude(false);
      group.setOcclusionRadius(earth.getOcclusionRadius());
      const fixed = group.addController(Pioneer.FixedController);
      fixed.setPosition(Pioneer.Vector3.Zero);
      fixed.setOrientation(Pioneer.Quaternion.Identity);
      group.addController(Pioneer.RotateByEntityOrientationController);

      for (const id in PLACEMARK_DATA) {
        const data = PLACEMARK_DATA[id];
        const altitude = 10;
        const entity = Placemarks.addPlacemark(
          id,
          id,
          earth,
          data.latLng[0],
          data.latLng[1],
          altitude,
        );
        entity.setEnabled(cityNames);

        entity.setParent(group); // Put placemark into group
        const div = entity.getComponent(Pioneer.DivComponent).getDiv();
        entity
          .getComponent(Pioneer.DivComponent)
          .setAlignment(new Pioneer.Vector2(0.5, 0.5));
        div.id = id;
        div.classList.add("placemark");
        div.classList.remove("pioneer-label-div");
        div.innerHTML =
          '<div class="pin"><img class="no-drag" src="assets/images/map_marker.png"></div>' +
          '<div class="content">' +
          data.text +
          "</div>";
      }
    }
  }

  createregionsPlacemarks() {
    const { regions } = layersStore.stateSnapshot;

    const earth = this._scene.getEntity("earth");
    const placemarks = this._scene.get("Regionplacemarks");
    if (!placemarks) {
      const group = this._scene.addEntity("Regionplacemarks");
      group.setParent(earth);
      group.setCanOcclude(false);
      group.setOcclusionRadius(earth.getOcclusionRadius());
      const fixed = group.addController(Pioneer.FixedController);
      fixed.setPosition(Pioneer.Vector3.Zero);
      fixed.setOrientation(Pioneer.Quaternion.Identity);
      group.addController(Pioneer.RotateByEntityOrientationController);

      for (const id in PLACEMARK_DATA_REGIONS) {
        const data = PLACEMARK_DATA_REGIONS[id];
        const altitude = 10;
        const entity = Placemarks.addPlacemark(
          id,
          id,
          earth,
          data.latLng[0],
          data.latLng[1],
          altitude,
        );
        entity.setEnabled(regions);

        entity.setParent(group); // Put placemark into group

        const divEntity = entity.getComponent(Pioneer.DivComponent);
        divEntity.setAlignment(new Pioneer.Vector2(0.5, 0.5));
        const div = divEntity.getDiv();
        div.id = id;
        div.classList.add("placemark");
        div.classList.remove("pioneer-label-div");
        div.innerHTML =
          '<div class="pin"><img class="no-drag" src="assets/images/map_marker.png"></div>' +
          '<div class="content">' +
          data.text +
          "</div>";
      }
    }
  }

  createEarthquakesPlacemarks() {
    const { earthquake } = layersStore.stateSnapshot;
    const earth = this._scene.getEntity("earth");
    const placemarks = this._scene.get("Earthquakesplacemarks");

    // Create tooltip container at body level (above everything)
    if (!document.getElementById("earthquake-tooltips-container")) {
      const tooltipContainer = document.createElement("div");
      tooltipContainer.id = "earthquake-tooltips-container";
      tooltipContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 999999;
        `;
      document.body.appendChild(tooltipContainer);
    }

    if (!placemarks) {
      const group = this._scene.addEntity("Earthquakesplacemarks");
      group.setParent(earth);
      group.setCanOcclude(false);
      group.setOcclusionRadius(earth.getOcclusionRadius());

      const fixed = group.addController(Pioneer.FixedController);
      fixed.setPosition(Pioneer.Vector3.Zero);
      fixed.setOrientation(Pioneer.Quaternion.Identity);
      group.addController(Pioneer.RotateByEntityOrientationController);
      function getSizeFromMagnitude(mag) {
        return Math.pow(mag, 1.5);
      }

      function getColorFromDepthAndMagnitude(depth, magnitude) {
        let baseColor;
        if (depth <= 10) baseColor = [255, 128, 0];
        else if (depth <= 20) baseColor = [255, 255, 0];
        else if (depth <= 50) baseColor = [0, 255, 0];
        else if (depth <= 100) baseColor = [0, 128, 255];
        else if (depth <= 150) baseColor = [128, 0, 255];
        else baseColor = [255, 0, 0];

        let intensity;
        if (magnitude < 2.0) intensity = 0.2;
        else if (magnitude < 3.0) intensity = 0.35;
        else if (magnitude < 4.0) intensity = 0.5;
        else if (magnitude < 5.0) intensity = 0.7;
        else if (magnitude < 6.0) intensity = 0.85;
        else intensity = 1.0;

        return baseColor.map((c) => Math.round(c * intensity));
      }

      // Function to position tooltip relative to circle
      window.positionEarthquakeTooltip = function (tooltipId, circleId) {
        const tooltip = document.getElementById(tooltipId);
        const circle = document.getElementById(circleId);

        if (!tooltip || !circle) return;

        const circleRect = circle.getBoundingClientRect();
        const tooltipWidth = tooltip.offsetWidth || 220;
        const tooltipHeight = tooltip.offsetHeight || 150;

        // Calculate center of circle
        const circleCenterX = circleRect.left + circleRect.width / 2;
        const circleCenterY = circleRect.top + circleRect.height / 2;

        // Position above the circle by default
        let finalTop = circleRect.top - tooltipHeight - 10;
        let finalLeft = circleCenterX - tooltipWidth / 2;

        // If goes above viewport, show below
        if (finalTop < 10) {
          finalTop = circleRect.bottom + 10;
        }

        // If goes below viewport, clamp
        if (finalTop + tooltipHeight > window.innerHeight - 10) {
          finalTop = window.innerHeight - tooltipHeight - 10;
        }

        // If goes off right edge
        if (finalLeft + tooltipWidth > window.innerWidth - 10) {
          finalLeft = window.innerWidth - tooltipWidth - 10;
        }

        // If goes off left edge
        if (finalLeft < 10) {
          finalLeft = 10;
        }

        // Apply position immediately
        tooltip.style.top = finalTop + "px";
        tooltip.style.left = finalLeft + "px";
      };

      // Close all tooltips function
      window.closeAllEarthquakeTooltips = function () {
        document.querySelectorAll(".earthquake-tooltip").forEach((tooltip) => {
          tooltip.style.display = "none";
        });
      };

      // Function to show tooltip
      window.showEarthquakeTooltip = function (tooltipId, circleId) {
        // Hide all other tooltips
        document.querySelectorAll(".earthquake-tooltip").forEach((t) => {
          t.style.display = "none";
        });

        const tooltip = document.getElementById(tooltipId);
        if (tooltip) {
          tooltip.style.display = "block";

          requestAnimationFrame(() => {
            window.positionEarthquakeTooltip(tooltipId, circleId);
          });
        }
      };

      // Function to toggle tooltip
      window.toggleEarthquakeTooltip = function (tooltipId, circleId) {
        const tooltip = document.getElementById(tooltipId);
        if (tooltip.style.display === "block") {
          tooltip.style.display = "none";
        } else {
          window.showEarthquakeTooltip(tooltipId, circleId);
        }
      };

      // Close on click and drag
      let isMouseDown = false;
      let dragStartX = 0;
      let dragStartY = 0;

      const handleMouseDown = (e) => {
        const isOverTooltip = e.target.closest(".earthquake-tooltip");
        const isOverCircle = e.target.closest(".circle-indicator");

        if (!isOverTooltip && !isOverCircle) {
          isMouseDown = true;
          dragStartX = e.clientX;
          dragStartY = e.clientY;
        }
      };

      const handleMouseMove = (e) => {
        if (isMouseDown) {
          // Calculate distance dragged
          const deltaX = Math.abs(e.clientX - dragStartX);
          const deltaY = Math.abs(e.clientY - dragStartY);

          // If dragged more than 5 pixels, close tooltips
          if (deltaX > 50 || deltaY > 50) {
            window.closeAllEarthquakeTooltips();
          }
        }
      };

      const handleMouseUp = () => {
        isMouseDown = false;
      };

      // Add event listeners for click and drag
      document.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      // Also handle touch events for mobile
      // document.addEventListener('touchstart', (e) => {
      //     const touch = e.touches[0];
      //     const isOverTooltip = e.target.closest('.earthquake-tooltip');
      //     const isOverCircle = e.target.closest('.circle-indicator');

      //     if (!isOverTooltip && !isOverCircle) {
      //         isMouseDown = true;
      //         dragStartX = touch.clientX;
      //         dragStartY = touch.clientY;
      //     }
      // });

      // document.addEventListener('touchmove', (e) => {
      //     if (isMouseDown && e.touches.length > 0) {
      //         const touch = e.touches[0];
      //         const deltaX = Math.abs(touch.clientX - dragStartX);
      //         const deltaY = Math.abs(touch.clientY - dragStartY);

      //         if (deltaX > 5 || deltaY > 5) {
      //             window.closeAllEarthquakeTooltips();
      //         }
      //     }
      // });

      // document.addEventListener('touchend', () => {
      //     isMouseDown = false;
      // });

      // Update tooltip positions continuously to follow dots
      const updateAllTooltips = () => {
        const tooltips = document.querySelectorAll(".earthquake-tooltip");

        tooltips.forEach((tooltip) => {
          if (tooltip.style.display === "block") {
            const tooltipId = tooltip.id;
            const circleId = tooltipId.replace("tooltip-", "circle-");
            const circle = document.getElementById(circleId);

            // Check if circle exists and is visible
            if (circle) {
              const circleRect = circle.getBoundingClientRect();
              const circleVisible =
                circleRect.width > 0 && circleRect.height > 0;

              // If circle is off-screen or behind globe, fade out
              const isOffScreen =
                circleRect.right < 0 ||
                circleRect.left > window.innerWidth ||
                circleRect.bottom < 0 ||
                circleRect.top > window.innerHeight ||
                circleRect.width < 2;

              if (isOffScreen || !circleVisible) {
                tooltip.style.opacity = "0";
                tooltip.style.pointerEvents = "none";
              } else {
                tooltip.style.opacity = "1";
                tooltip.style.pointerEvents = "auto";
                // Update position every frame
                window.positionEarthquakeTooltip(tooltipId, circleId);
              }
            } else {
              // Circle doesn't exist, hide tooltip
              tooltip.style.display = "none";
            }
          }
        });
      };

      // Continuous update loop - tooltip follows dot smoothly at 60fps
      let rafId;
      const updateLoop = () => {
        updateAllTooltips();
        rafId = requestAnimationFrame(updateLoop);
      };

      // Start the update loop
      updateLoop();

      for (const id in PLACEMARK_DATA_EARTHQUAKES) {
        if (id > 10) break;
        const data = PLACEMARK_DATA_EARTHQUAKES[id];
        const altitude = 10;
        const circleSize = getSizeFromMagnitude(data.magnitude);
        const color = getColorFromDepthAndMagnitude(data.depth, data.magnitude);
        const parseSafeDate = (value) => {
          if (!value) return null;

          const normalized = value.replace(" UTC", "Z").replace(" ", "T");

          const d = new Date(normalized);
          return isNaN(d.getTime()) ? null : d.getTime();
        };
        const time = parseSafeDate(data.time);

        // const entity = Placemarks.addPlacemark(
        //     id,
        //     id,
        //     earth,
        //     data.latLng[0],
        //     data.latLng[1],
        //     altitude,
        //     data.time
        // );

        // let group = this._scene.getEntity("Earthquakesplacemarks");
        // // const eq_component = group.addComponent(Pioneer.SpheroidComponent);
        // // eq_component.setEnabled(true)
        // // console.log("eq_component", eq_component)
        // const entity = group.addComponent(Pioneer.LabelComponent)
        // entity.setText("⬤")
        // entity.setColor(new Pioneer.Color(color[0], color[1], color[2]))
        // //set position of label
        // const centerLLA = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(data.latLng[0])),
        //    Pioneer.MathUtils.degToRad(parseFloat(data.latLng[1])), 0.01 )
        // const center = new Pioneer.Vector3();
        // // const earthSpheroidComponent = earth.getComponent(Pioneer.SpheroidComponent);
        // // earthSpheroidComponent.xyzFromLLA(center, centerLLA)
        // // eq_component.setPosition(center)
        // entity.setIgnoreDistance(true);

        const a = Math.floor(Math.random() * 31) - 15;
        const b = Math.floor(Math.random() * 31) - 15;

        // entity.setAlignment(new Pioneer.Vector2(a,b))

        // const earth = this._scene.getEntity("earth");
        let group = this._scene.getEntity("Earthquakesplacemarks");
        const entity = group.addComponent(Pioneer.SpriteComponent, `eq_${id}`);
        entity.setBillboard(true);
        entity.setTextureUrl(`assets/images/map_marker.png`);
        entity.setAlignment(new Pioneer.Vector2(a, b));
        entity.setSizeUnits("pixels");
        entity.setSize(new Pioneer.Vector2(20, 20));

        // entity._timeValue = parseSafeDate(data.time);
        entity.setEnabled(true);
        // entity.setParent(group);

        // const div =  entity.getComponent(Pioneer.DivComponent).getDiv();

        // entity
        // .getComponent(Pioneer.DivComponent)
        // .setAlignment(new Pioneer.Vector2(0.5, 0.5));
        // div.id = id;
        // div.classList.add('placemark');
        // div.classList.remove('pioneer-label-div');

        // Circle in the placemark
        // div.innerHTML = `
        //     <div style="
        //         position: relative;
        //         pointer-events: auto;
        //     ">
        //         <div
        //             id="circle-${id}"
        //             class="circle-indicator"
        //             style="
        //                 width: ${circleSize}px;
        //                 height: ${circleSize}px;
        //                 border: 1px solid #fff;
        //                 border-radius: 50%;
        //                 background-color: ${color};
        //                 margin: auto;
        //                 cursor: pointer;
        //                 position: relative;
        //             "
        //             onclick="event.stopPropagation(); window.toggleEarthquakeTooltip('tooltip-${id}', 'circle-${id}');"
        //         ></div>
        //     </div>
        // `;

        // Tooltip in the fixed container at body level
        const tooltipContainer = document.getElementById(
          "earthquake-tooltips-container",
        );
        const tooltipDiv = document.createElement("div");
        tooltipDiv.id = `tooltip-${id}`;
        tooltipDiv.className = "earthquake-tooltip";
        tooltipDiv.style.cssText = `
                display: none;
                position: fixed;
                width: 220px;
                text-align: right;
                background-color: #1d2f4dd1;
                border-radius: 8px;
                padding: 10px;
                padding-top: 30px;
                pointer-events: auto;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
                transition: opacity 0.2s ease;
            `;

        tooltipDiv.innerHTML = `
                <div style="
                    position: absolute;
                    top: 8px;
                    left: 8px;
                    cursor: pointer;
                    color: #fff;
                    font-weight: bold;
                    font-size: 18px;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                " onclick="document.getElementById('tooltip-${id}').style.display='none'">
                    ✕
                </div>
                <p style='font-size: 20px; margin: 5px 0; color: #fff;'>${data.place}</p>
                <p style="color: #fff; margin: 5px 0; font-size: 14px;">${data.info}</p>
                <p style="color: #aaa; margin: 5px 0; font-size: 12px;">
                    <strong>القوة:</strong> ${data.magnitude} | 
                    <strong>العمق:</strong> ${data.depth} كم
                </p>
                <a href="${data.link}" target="_blank" style="color: #4dabf7; text-decoration: none;">المصدر</a>
            `;

        tooltipContainer.appendChild(tooltipDiv);
      }

      // Clean up on component destroy
      this._cleanupEarthquakeTooltips = () => {
        if (rafId) cancelAnimationFrame(rafId);
        document.removeEventListener("mousedown", handleMouseDown);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }
  createDemModel() {
    const { dem } = layersStore.stateSnapshot;
    const demData = this._getSelectedDemData();
    if (!demData) {
      return;
    }

    const earth = this._scene.get("earth");
    let entity = this._scene.get("demModel");
    if (!entity) {
      entity = this._scene.addEntity("demModel");
      entity.setParent(earth);
      entity.setCanOcclude(false);
    }

    entity.setExtentsRadius(demData.extentsRadius || 0);
    const spheroid = earth.getComponent(Pioneer.SpheroidComponent);

    const { lat, lon } = this._getDemCenter();

    const lla = new Pioneer.LatLonAlt(
      Pioneer.MathUtils.degToRad(lat || 0),
      Pioneer.MathUtils.degToRad(lon || 0),
      demData.height || 0,
    );
    const position = new Pioneer.Vector3();
    spheroid.xyzFromLLA(position, lla);
    // Orient the DEM so its local up aligns with the surface normal at this point.
    const normal = new Pioneer.Vector3(position.x, position.y, position.z);
    normal.normalize(normal);
    // Assume model up is +Y; rotate +Y to the surface normal.
    const orient = Pioneer.Quaternion.fromVectorFromTo(
      Pioneer.Vector3.YAxis,
      normal,
    );
    const tiltDegreesX = Number(demData.tiltDegreesX) || 0;
    const tiltDegreesY = Number(demData.tiltDegreesY) || 0;
    const tiltDegreesZ = Number(demData.tiltDegreesZ) || 0;
    let finalOrient = orient;
    if (tiltDegreesX !== 0) {
      const tiltRad = Pioneer.MathUtils.degToRad(tiltDegreesX);
      const half = tiltRad / 2;
      const sin = Math.sin(half);
      const cos = Math.cos(half);
      const tiltQuat = new Pioneer.Quaternion(cos, sin, 0, 0);
      finalOrient =
        this._multiplyQuaternionsWXYZ(finalOrient, tiltQuat) || finalOrient;
    }
    if (tiltDegreesY !== 0) {
      const tiltRad = Pioneer.MathUtils.degToRad(tiltDegreesY);
      const half = tiltRad / 2;
      const sin = Math.sin(half);
      const cos = Math.cos(half);
      const tiltQuat = new Pioneer.Quaternion(cos, 0, sin, 0);
      finalOrient =
        this._multiplyQuaternionsWXYZ(finalOrient, tiltQuat) || finalOrient;
    }
    if (tiltDegreesZ !== 0) {
      const tiltRad = Pioneer.MathUtils.degToRad(tiltDegreesZ);
      const half = tiltRad / 2;
      const sin = Math.sin(half);
      const cos = Math.cos(half);
      const tiltQuat = new Pioneer.Quaternion(cos, 0, 0, sin);
      finalOrient =
        this._multiplyQuaternionsWXYZ(finalOrient, tiltQuat) || finalOrient;
    }
    this._demModelLocalPosition = new Pioneer.Vector3(
      position.x,
      position.y,
      position.z,
    );
    this._demModelLocalOrientation = new Pioneer.Quaternion(
      finalOrient.w,
      finalOrient.x,
      finalOrient.y,
      finalOrient.z,
    );
    this._ensureDemTransformSync();
    this._syncDemEntitiesToEarthOrientation();
    let model = entity.getComponent(Pioneer.ModelComponent);
    if (!model) {
      model = entity.addComponent(Pioneer.ModelComponent);
    }
    const scale = demData.scale ?? 0.001;
    const verticalExaggeration = demData.verticalExaggeration ?? 1;
    const flipX = demData.flipX ? -1 : 1;
    const flipZ = demData.flipZ ? -1 : 1;
    const activeDemModelId = demData.id;
    model.setResourcesLoadedCallback(() => {
      if (this._getSelectedDemData()?.id !== activeDemModelId) {
        return;
      }
      this._configureDemMaterialAppearance(model);
      this._applyDemLightingBoost(true);
      console.log("DEM model loaded", {
        id: activeDemModelId,
        radius: model.getRadius?.(),
        scale,
      });
    });
    model.setUrl(demData.url);
    model.setScale(
      new Pioneer.Vector3(
        scale * flipX,
        scale * verticalExaggeration,
        scale * flipZ,
      ),
    );
    if (
      entity &&
      this._demModelLocalPosition &&
      this._demModelLocalOrientation
    ) {
      this._ensureDemTransformSync();
      this._syncDemEntitiesToEarthOrientation();
    }
    const demPlacemark = this._ensureDemPlacemark();
    demPlacemark?.setEnabled(dem);
    entity.setEnabled(dem);
  }

  _configureDemMaterialAppearance(model) {
    const materials = model.getThreeJsMaterials?.() || [];
    const maxAnisotropy =
      this._pioneer
        .__getThreeJsRenderer?.()
        .capabilities?.getMaxAnisotropy?.() || 16;

    for (let i = 0; i < materials.length; i++) {
      const material = materials[i];
      const uniforms = material?.uniforms;
      if (!uniforms) {
        continue;
      }

      const colorTexture = uniforms.colorTexture?.value;
      if (colorTexture) {
        colorTexture.anisotropy = Math.max(
          colorTexture.anisotropy || 1,
          maxAnisotropy,
        );
        colorTexture.generateMipmaps = true;
        colorTexture.needsUpdate = true;

        uniforms.emissiveTexture.value = colorTexture;
        if (material.defines) {
          material.defines.emissiveMap = true;
        }
        material.needsUpdate = true;
      }

      // Render the DEM as color-accurate map.
      uniforms.color?.value?.setRGB(0, 0, 0);
      uniforms.emissiveColor?.value?.set(1, 1, 1);
      uniforms.roughness && (uniforms.roughness.value = 1);
      uniforms.metalness && (uniforms.metalness.value = 0);
      uniforms.environmentIntensity &&
        (uniforms.environmentIntensity.value = 0);
    }
  }

  _applyDemLightingBoost(enabled) {
    if (!enabled) return;

    // 1) Bright ambient light (works even if camera light is dim)
    // If something else later overwrites ambient, calling this again keeps DEM readable.
    const ambient = new Pioneer.Color(0.75, 0.75, 0.75);
    this._scene.setAmbientLightColor(ambient);

    // 2) Bright camera lights (covers the non-spout path)
    for (let i = 0; i < this._pioneer.getNumViewports(); i++) {
      const camEntity = this._pioneer.getViewportAt(i).getCamera().getEntity();
      const lightSource = camEntity.getComponent(Pioneer.LightSourceComponent);
      if (lightSource) {
        lightSource.setColor(new Pioneer.Color(1, 1, 1));
        lightSource.setIntensity?.(1.0);
      }
    }
  }

  /**
   * Set earth lighting
   * @param {boolean} state - earth lighting state
   */
  async setEarthLighting(state) {
    const earth = this._scene.getEntity("earth");
    await earth.waitTillInPlace();
    const { lightColorOn, lightColorOff } = globalRefs;

    const earthComponents = earth.getNumChildren();

    for (let i = 0; i < earthComponents; i++) {
      const comp = earth.getComponentAt(i);

      if (typeof comp?.["setAmbientLightColor"] === "function") {
        comp.setAmbientLightColor(state ? lightColorOn : lightColorOff);
      }
    }
  }

  /**
   * Set camera lighting
   * @param {boolean} state - camera lighting
   */
  setCameraLighting(state) {
    const camera = this._scene.getEntity("camera");

    if (!camera) return;

    const cameraLight =
      camera.getComponent(Pioneer.LightSourceComponent) ||
      camera.addComponent(Pioneer.LightSourceComponent);
    cameraLight.setEnabled(state);
  }
  /**
   * Create an event placemark is does not exist
   * @param {object} event
   * @returns {object} event
   */
  singleEventPlacemark(event) {
    const { id, boundaries, title, urlParam } = event || {};
    const { north, south, east, west } = boundaries || {};
    // console.log("singleEventPlacemark")
    if (!id || !boundaries || SAND_EVENT_IDS.includes(id)) {
      return;
    }

    const earth = this._scene.getEntity("earth");
    let group = this._scene.getEntity("events");

    if (!group) {
      group = this._scene.addEntity("events");
      group.setParent(earth);
      group.setCanOcclude(false);
      group.setExtentsRadius(earth.getExtentsRadius());
      const fixed = group.addController(Pioneer.FixedController);
      fixed.setPosition(Pioneer.Vector3.Zero);
      fixed.setOrientation(Pioneer.Quaternion.Identity);
      group.addController(Pioneer.RotateByEntityOrientationController);
    }

    let entity = this._scene.getEntity(id);
    if (!entity) {
      const lat = (south + north) / 2;
      const lon = (west + east) / 2;
      entity = Placemarks.addPlacemark(id, "", earth, lat, lon, 0);
      const entityEl = entity.getComponent(Pioneer.DivComponent);

      // Put placemark into group.
      entity.setParent(group);

      const div = entityEl.getDiv();
      entityEl.setAlignment(new Pioneer.Vector2(0.5, 0.5));
      div.id = `eventPlacemark_${id}`;
      div.classList.add("pin", "event-pin");
      div.classList.remove("pioneer-label-div");
      div.title = title;
      div.classList.add("placemark");
      div.innerHTML =
        '<div class="pin"><img class="no-drag" src="assets/images/map_marker_events.svg"></div>';
      div.addEventListener("click", () => {
        // Navigate to the event.
        const { getManager } = globalRefs;
        getManager("route").navigateToEvent(urlParam);
      });
    }

    return entity;
  }

  /**
   * Show / Hide event placemarks.
   *
   * @param {boolean} value
   * @param {Array} eventList - optional list (otherwise all event placemarks)
   */
  displayEventPlacemarks(value, eventList = this._displayingEvents) {
    const { spout } = spoutStore.stateSnapshot;

    if (!eventList) {
      return;
    }

    const cameraEntity = this._scene.getEntity("camera");
    const cameraParent = cameraEntity?.getParent()?.getName();

    // If showing placemarks, store the this._displayingEvents = [] so we can easily hide them later.
    if (value) {
      this._displayingEvents = [...eventList];
    }

    const handleEntity = (entity, value) => {
      if (!entity) {
        return;
      }

      // Set entity enabled.
      entity.setEnabled(value);

      // Set placemark enabled.
      const placemarkDiv = entity.getComponent(Pioneer.DivComponent);
      placemarkDiv.setEnabled(value);

      // If Spout is on, update visibility of spout labels as well as regular placemarks
      if (spout) {
        const entityLabel = entity.getComponent(Pioneer.LabelComponent);
        entityLabel?.setEnabled(value);
      }
    };

    eventList.forEach(async (event) => {
      const entity = this.singleEventPlacemark(event);

      if (!entity) {
        return;
      }

      // If the camera is parented to the entity, we don't want to enable/disable it until the camera has finished transitioning.
      if (entity.getName() === cameraParent) {
        await this._scene.waitTillEntitiesInPlace(["earth", event.id]);

        const { isCameraTransitioning } = cameraStore.stateSnapshot;

        if (isCameraTransitioning) {
          // Wait for the camera transition to finish.
          const transitionUnsubscribe = cameraStore.subscribeTo(
            "isCameraTransitioning",
            (isCameraTransitioning) => {
              if (isCameraTransitioning === false) {
                transitionUnsubscribe();
                handleEntity(entity, value);
              }
            },
          );
        } else {
          handleEntity(entity, value);
        }
      } else {
        handleEntity(entity, value);
      }
    });
  }

  singleCustomPlacemark(item, is_poi) {
    const { id, center, title, external_id } = item || {};
    // console.log("singleCustomPlacemark", item)

    if (!external_id || !center) {
      return;
    }

    const earth = this._scene.getEntity("earth");
    let group = this._scene.getEntity("custom_placemarks");

    if (!group) {
      group = this._scene.addEntity("custom_placemarks");
      group.setParent(earth);
      group.setCanOcclude(false);
      group.setExtentsRadius(earth.getExtentsRadius());
      const fixed = group.addController(Pioneer.FixedController);
      fixed.setPosition(Pioneer.Vector3.Zero);
      fixed.setOrientation(Pioneer.Quaternion.Identity);
      group.addController(Pioneer.RotateByEntityOrientationController);
    }

    let entity = this._scene.get(external_id);
    if (!entity) {
      const center_arr = center.split(",");
      const lat = Number(center_arr[0]);
      const lon = Number(center_arr[1]);
      // console.log("singleCustomPlacemark", {external_id, is_poi, item})
      entity = Placemarks.addPlacemark(external_id, "", earth, lat, lon, 0);
      const entityEl = entity.getComponent(Pioneer.DivComponent);
      // Put placemark into group.
      entity.setParent(group);

      const div = entityEl.getDiv();
      entityEl.setAlignment(new Pioneer.Vector2(0.5, 0.5));
      div.id = "eventPlacemark_" + external_id;
      div.classList.add("pin", "eventPin", "placemark", "custom_placemark");
      div.classList.remove("pioneer-label-div");
      div.title = title;
      div.classList.add("placemark");
      if (is_poi) {
        //div.innerHTML = '<div class="pin"><img class="no-drag" src="assets/images/map_marker_poi.svg"></div>';
        div.addEventListener("click", () => {
          const { getManager } = globalRefs;
          getManager("route").navigateToPoi(external_id);
        });
      } else {
        //div.innerHTML = '<div class="pin"><img class="no-drag" src="assets/images/map_marker_gmap.svg"></div>';
        div.addEventListener("click", () => {
          const { getManager } = globalRefs;
          getManager("route").navigateToGmap(external_id);
        });
      }
    }

    return entity;
  }

  singleGmapPlacemark(eventId = null) {
    if (!eventId) {
      return;
    }

    if (!this._gmapIds.includes(eventId)) {
      this._gmapIds.push(eventId);
    }

    const earth = this._scene.get("earth");
    let group = this._scene.getEntity("events");

    if (!group) {
      group = this._scene.addEntity("events");
      group.setParent(earth);
      group.setCanOcclude(false);
      group.setExtentsRadius(earth.getExtentsRadius());
      const fixed = group.addController(Pioneer.FixedController);
      fixed.setPosition(Pioneer.Vector3.Zero);
      fixed.setOrientation(Pioneer.Quaternion.Identity);
      group.addController(Pioneer.RotateByEntityOrientationController);
    }

    let entity = this._scene.get(eventId);

    if (!entity) {
      const { getGmapInfo } = get("content");
      const event = getGmapInfo(eventId);

      if (!event || !event.center) {
        return null;
      }

      const lat = Number(event.center.split(",")[0]);
      const lon = Number(event.center.split(",")[1]);
      entity = Placemarks.addPlacemark(
        eventId,
        "",
        earth,
        lat,
        lon,
        0,
        true,
        true,
      );
      const entityEl = entity.getComponent(Pioneer.DivComponent);
      // Put placemark into group.
      entity.setParent(group);

      const div = entityEl.getDiv();
      div.id = "eventPlacemark_" + event.id;
      // div.classList.add('pin');
      // div.classList.add('eventPin');
      div.classList.add("placemark");
      // div.innerHTML = '<div class="pin"><img class="no-drag" src="assets/images/map_marker_gmap.svg"></div>';
      div.addEventListener("click", () => {
        gmapStore.setGlobalState({
          showOnGlobeGmap: false,
          showLatestGmap: false,
        });
        this._router.goToGmap(eventId);
      });
    }

    return entity;
  }

  displayCustomPlacemarks(value, is_poi, list) {
    const { spout } = spoutStore.stateSnapshot;
    if (!list) {
      list = is_poi ? this._displayingPois : this._displayingGmaps;
    }

    const cameraEntity = this._scene.get("camera");
    const cameraParent = cameraEntity?.getParent()?.getName();

    // If showing placemarks, store the this._displayingEvents = [] so we can easily hide them later.
    if (value) {
      if (is_poi) {
        this._displayingPois = [...list];
      } else {
        this._displayingGmaps = [...list];
      }
    }

    const handleEntity = (entity, value) => {
      if (!entity) {
        return;
      }

      // Set entity enabled.
      entity.setEnabled(value);

      // Set placemark enabled.
      const placemarkDiv = entity.getComponent(Pioneer.DivComponent);
      placemarkDiv.setEnabled(value);

      // If Spout is on, update visibility of spout labels as well as regular placemarks
      if (spout) {
        const entityLabel = entity.get("label");
        entityLabel?.setEnabled(value);
      }
    };

    list.forEach(async (item) => {
      const entity = this.singleCustomPlacemark(item, is_poi);

      if (!entity) {
        return;
      }

      // If the camera is parented to the entity, we don't want to enable/disable it until the camera has finished transitioning.
      if (entity.getName() === cameraParent) {
        await SceneHelpers.waitTillEntitiesInPlace(this._scene, [
          "earth",
          item.external_id,
        ]);

        const { isCameraTransitioning } = cameraStore.stateSnapshot;

        if (isCameraTransitioning) {
          // Wait for the camera transition to finish.
          const transitionUnsubscribe = cameraStore.subscribeTo(
            "isCameraTransitioning",
            (isCameraTransitioning) => {
              if (isCameraTransitioning === false) {
                transitionUnsubscribe();
                handleEntity(entity, value);
              }
            },
          );
        } else {
          handleEntity(entity, value);
        }
      } else {
        handleEntity(entity, value);
      }
    });
  }
}

export default SceneManager;
